// Use Netlify function as proxy to avoid CORS issues
const CLAUDE_API_URL = '/.netlify/functions/claude-api';

export async function generateUserProgram(userData, previousWeekData = null) {
  const prompt = buildOnboardingPrompt(userData, previousWeekData);

  try {
    const response = await fetch(CLAUDE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 4096,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to generate program from Claude');
    }

    const data = await response.json();
    const programText = data.content[0].text;

    // Parse JSON from Claude's response
    let jsonStr = programText;
    const jsonMatch = programText.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      jsonStr = jsonMatch[1];
    }

    return JSON.parse(jsonStr);
  } catch (error) {
    console.error('Claude API error:', error);
    throw error;
  }
}

function buildOnboardingPrompt(userData, previousWeekData = null) {
  const {
    displayName,
    age,
    gender,
    height,
    weight,
    fitnessLevel,
    pullups,
    pushups,
    bodyFat,
    primaryGoal,
    targetAreas,
    timeframe,
    daysPerWeek,
    timePerSession,
    homeWorkoutTime,
    gymWorkoutTime,
    gymDays,
    restDays,
    gymAccess,
    equipment
  } = userData;

  const isWeeklyReview = previousWeekData !== null;
  const weekNumber = previousWeekData ? previousWeekData.weekNumber : 1;
  const isPostAssessment = previousWeekData?.isPostAssessmentWeek || false;

  return `You are a professional personal trainer ${isWeeklyReview ? 'reviewing progress and adjusting' : 'designing'} a completely personalized training program.

${isPostAssessment ? `**POST-ASSESSMENT WEEK ANALYSIS - DESIGNING WEEK ${weekNumber + 1} PROGRAM:**

${displayName} just completed Week 1 Assessment designed specifically for their goal (${primaryGoal}). You now have REAL performance data to work with, not just self-reported estimates.

**CRITICAL: USE THIS ASSESSMENT DATA TO INFORM YOUR PROGRAM:**

${previousWeekData.assessmentData ? `
**ASSESSMENT WEEK PERFORMANCE DATA:**
${JSON.stringify(previousWeekData.assessmentData, null, 2)}

**How to Use This Data:**
1. **Strength Baselines:** Use recorded weights/reps to set appropriate starting loads
   - Don't guess - you have actual performance data now
   - Example: If they did 150lbs x 3 on squats, calculate their working weights from that

2. **Volume Tolerance:** Look at how they handled the assessment week
   - Did they complete all workouts? Shows good work capacity
   - Difficulty ratings consistently 8-10? May need to reduce volume initially
   - Skipped workouts? May need different schedule or more rest days

3. **Movement Quality & Imbalances:**
   - Note any exercises where performance was notably weak
   - Bilateral exercises: did one side struggle more?
   - Address these in your program design

4. **Conditioning Level:**
   - Cardio tests show current aerobic/anaerobic capacity
   - Sprint times, circuit performance, endurance tests inform conditioning work

5. **Recovery Capacity:**
   - How did they recover day-to-day during assessment week?
   - This guides frequency and volume decisions

**DO NOT:**
- Ignore this data and create a generic program
- Use the self-reported "pullups: ${pullups}, pushups: ${pushups}" when you have actual assessment data
- Prescribe weights/reps that don't align with their demonstrated capacity

**DO:**
- Reference specific assessment exercises when setting training loads
- Build progressive overload from their actual baselines
- Address weaknesses and imbalances you observed
- Match volume and intensity to their demonstrated recovery capacity
` : ''}

` : ''}${isWeeklyReview && !isPostAssessment ? `**WEEKLY REVIEW - WEEK ${weekNumber} ANALYSIS:**` : !isWeeklyReview ? '**CLIENT PROFILE:**' : ''}
- Name: ${displayName}
- Age: ${age}, Gender: ${gender}
- Height: ${height}" (${Math.floor(height / 12)}' ${height % 12}")
- Weight: ${weight} lbs
- Fitness Level: ${fitnessLevel}
- Current Performance: ${pullups || 0} pull-ups, ${pushups || 0} push-ups (${isPostAssessment ? 'self-reported - USE ASSESSMENT DATA INSTEAD' : 'baseline estimates'})
${bodyFat ? `- Body Fat: ${bodyFat}%` : ''}

${isWeeklyReview ? `
**PREVIOUS WEEK'S DATA (Week ${previousWeekData.weekNumber}):**

**Body Composition Changes:**
- Weight: ${previousWeekData.startWeight} lbs → ${weight} lbs (${weight - previousWeekData.startWeight > 0 ? '+' : ''}${(weight - previousWeekData.startWeight).toFixed(1)} lbs)
${previousWeekData.startBodyFat && bodyFat ? `- Body Fat: ${previousWeekData.startBodyFat}% → ${bodyFat}% (${bodyFat - previousWeekData.startBodyFat > 0 ? '+' : ''}${(bodyFat - previousWeekData.startBodyFat).toFixed(1)}%)` : ''}

**Performance Metrics:**
- Pull-ups: ${previousWeekData.startPullups || 0} → ${pullups || 0} (${(pullups || 0) - (previousWeekData.startPullups || 0) > 0 ? '+' : ''}${(pullups || 0) - (previousWeekData.startPullups || 0)})
- Push-ups: ${previousWeekData.startPushups || 0} → ${pushups || 0} (${(pushups || 0) - (previousWeekData.startPushups || 0) > 0 ? '+' : ''}${(pushups || 0) - (previousWeekData.startPushups || 0)})

${previousWeekData.startMeasurements?.restingHeartRate && userData.restingHeartRate ? `**Cardiovascular Health:**
- Resting Heart Rate: ${previousWeekData.startMeasurements.restingHeartRate} bpm → ${userData.restingHeartRate} bpm (${userData.restingHeartRate - previousWeekData.startMeasurements.restingHeartRate > 0 ? '+' : ''}${userData.restingHeartRate - previousWeekData.startMeasurements.restingHeartRate})` : ''}
${previousWeekData.startMeasurements?.bloodPressureSystolic && userData.bloodPressureSystolic ? `- Blood Pressure: ${previousWeekData.startMeasurements.bloodPressureSystolic}/${previousWeekData.startMeasurements.bloodPressureDiastolic} → ${userData.bloodPressureSystolic}/${userData.bloodPressureDiastolic}` : ''}

${previousWeekData.startMeasurements && Object.keys(previousWeekData.startMeasurements).length > 0 ? `**Body Measurements (inches):**
${previousWeekData.startMeasurements.biceps && userData.biceps ? `- Biceps: ${previousWeekData.startMeasurements.biceps}" → ${userData.biceps}" (${userData.biceps - previousWeekData.startMeasurements.biceps > 0 ? '+' : ''}${(userData.biceps - previousWeekData.startMeasurements.biceps).toFixed(1)}")` : ''}
${previousWeekData.startMeasurements.chest && userData.chest ? `- Chest: ${previousWeekData.startMeasurements.chest}" → ${userData.chest}" (${userData.chest - previousWeekData.startMeasurements.chest > 0 ? '+' : ''}${(userData.chest - previousWeekData.startMeasurements.chest).toFixed(1)}")` : ''}
${previousWeekData.startMeasurements.waist && userData.waist ? `- Waist: ${previousWeekData.startMeasurements.waist}" → ${userData.waist}" (${userData.waist - previousWeekData.startMeasurements.waist > 0 ? '+' : ''}${(userData.waist - previousWeekData.startMeasurements.waist).toFixed(1)}")` : ''}
${previousWeekData.startMeasurements.hips && userData.hips ? `- Hips: ${previousWeekData.startMeasurements.hips}" → ${userData.hips}" (${userData.hips - previousWeekData.startMeasurements.hips > 0 ? '+' : ''}${(userData.hips - previousWeekData.startMeasurements.hips).toFixed(1)}")` : ''}
${previousWeekData.startMeasurements.thighs && userData.thighs ? `- Thighs: ${previousWeekData.startMeasurements.thighs}" → ${userData.thighs}" (${userData.thighs - previousWeekData.startMeasurements.thighs > 0 ? '+' : ''}${(userData.thighs - previousWeekData.startMeasurements.thighs).toFixed(1)}")` : ''}
${previousWeekData.startMeasurements.calves && userData.calves ? `- Calves: ${previousWeekData.startMeasurements.calves}" → ${userData.calves}" (${userData.calves - previousWeekData.startMeasurements.calves > 0 ? '+' : ''}${(userData.calves - previousWeekData.startMeasurements.calves).toFixed(1)}")` : ''}` : ''}

**WORKOUT COMPLETION:**
- Workouts Completed: ${previousWeekData.workoutsCompleted} / ${previousWeekData.workoutsScheduled}
- Completion Rate: ${((previousWeekData.workoutsCompleted / previousWeekData.workoutsScheduled) * 100).toFixed(0)}%
- Total Exercises Logged: ${previousWeekData.totalExercises}
${previousWeekData.skippedWorkouts > 0 ? `- Skipped Workouts: ${previousWeekData.skippedWorkouts}` : ''}

**PERFORMANCE TRENDS - SET-BY-SET ANALYSIS:**
${previousWeekData.performanceSummary || 'No detailed performance data available.'}

**CRITICAL - INTELLIGENT PROGRAM ADJUSTMENTS:**

You must analyze ALL data holistically to make smart, context-aware adjustments:

**1. PROGRESSIVE OVERLOAD - Advanced Set-by-Set Analysis:**

The performance data above shows INDIVIDUAL SETS for each exercise. Use this granular data to make intelligent recommendations:

**Analyzing Set-by-Set Patterns:**
- **Consistent Performance Across Sets** (e.g., Set 1: 20lbs×10, Set 2: 20lbs×10, Set 3: 20lbs×9)
  → User maintained weight well - ready for weight increase next week
- **Significant Drop-Off** (e.g., Set 1: 20lbs×12, Set 2: 20lbs×8, Set 3: 15lbs×6)
  → Starting weight too heavy OR rest periods too short - maintain or slightly reduce weight
- **Progressive Fatigue Pattern** (e.g., Set 1: 20lbs×12, Set 2: 20lbs×10, Set 3: 20lbs×8)
  → Normal fatigue, weight is appropriate - can increase by small amount (2.5-5 lbs)
- **Improving Across Week** (e.g., Session 1: 20lbs×8,8,7 → Session 2: 20lbs×10,9,8)
  → Clear strength adaptation - increase weight by 5-10 lbs
- **Stalling or Regressing** (e.g., Session 1: 25lbs×10,10,9 → Session 2: 25lbs×8,7,6)
  → May indicate inadequate recovery or overreaching - maintain or reduce weight, check recovery metrics

**Context-Aware Weight Progression Recommendations:**
- **Light weights (5-40 lbs):**
  - If difficulty 6-7 AND reps maintained across sets: +2.5-5 lbs
  - If difficulty 8+ OR significant rep drop-off: maintain weight, improve consistency first
  - If hitting top of rep range easily (e.g., 3×12 when range is 8-12): +5 lbs

- **Medium weights (40-100 lbs):**
  - If difficulty 6-7 AND good set consistency: +5-10 lbs
  - If difficulty 8+: maintain weight OR add 1-2 reps per set
  - If last set drops more than 30% from first set: too heavy, reduce by 5-10 lbs

- **Heavy weights (100+ lbs):**
  - If difficulty 6-7 AND completed all prescribed reps: +5-15 lbs OR add reps
  - If difficulty 8-9 but completed all sets: maintain weight, add reps instead
  - If difficulty 9-10: reduce weight by 10-15 lbs to improve form and consistency
  - Compound movements (squat, deadlift, bench): be conservative, focus on form

- **High-rep exercises (15+ reps):**
  - Focus on adding reps (2-5) rather than weight initially
  - Once hitting 20+ reps consistently, increase weight and drop back to 15 reps

- **Low-rep strength (4-8 reps):**
  - Weight increases should be small (2.5-5 lbs) to maintain rep range
  - If completing 3×8 easily, increase to 3×6 with heavier weight

**Using Fatigue Patterns for Intelligent Programming:**
- If "FATIGUE PATTERN" warning appears (>30% performance drop):
  - Prescribe longer rest periods (90-180 sec instead of 60 sec)
  - OR reduce weight by 10-15% to improve movement quality
  - OR prescribe fewer sets with same weight
- If "STRONG ENDURANCE" appears (<10% drop):
  - User ready for progressive overload
  - Can add weight OR add volume (extra set)
  - Consider higher-intensity techniques (drop sets, supersets)

**Using Volume Data:**
- Total Volume = weight × reps × sets
- If volume increasing week-to-week: positive adaptation
- If volume flat or decreasing despite good effort: check recovery metrics, may need deload
- Use volume to balance different exercises: if one exercise has huge volume increase, others should be more conservative

**Exercise-Specific Progression Strategy:**
For EACH exercise you prescribe, reference their previous performance if available:
- "Bench Press: You did 135lbs for sets of 10,8,7 last week (difficulty 7/10). I'm progressing you to 140lbs targeting 3×8. Focus on controlling the eccentric."
- "Goblet Squats: Last week you did 35lbs for 12,12,11 reps. This week try 40lbs for 3×10. If you can get 10+ on all sets, we'll go up next week."
- "Pull-ups: You managed 8,6,5 reps last session. Let's aim for 9,7,6 this week by focusing on explosive concentric."

**If Difficulty 8-10 Consistently:**
- User may be overtraining, under-recovering, or weights are too ambitious
- REDUCE volume by 10-20% or maintain weights instead of increasing
- Check sleep, stress, fatigue metrics - these explain high difficulty ratings

**2. BODY COMPOSITION & HEALTH METRICS ANALYSIS:**
Analyze the relationship between weight, body fat, measurements, and cardiovascular health:

**Body Composition:**
- **Weight UP + Body Fat UP + Strength stalling**: Eating too much, poor food quality, or insufficient protein
  → Reduce calories by 10-15%, increase protein, add metabolic conditioning
- **Weight DOWN + Strength DOWN significantly**: Losing muscle, cutting too aggressively
  → Increase calories, especially on training days, reduce cardio/conditioning
- **Weight stable + Body Fat DOWN + Strength UP**: PERFECT - maintain current approach
- **Weight UP + Body Fat stable/down + Strength UP**: Gaining muscle successfully - maintain or slight calorie increase

**Body Measurements Tell The Real Story:**
- **Biceps/Thighs/Calves increasing + Strength UP**: Muscle growth - excellent progress
- **Waist decreasing + Weight stable/down**: Losing fat - great for all goals
- **Waist increasing + Weight up**: Gaining fat - adjust nutrition if not intentional bulk
- **Chest increasing**: Could be muscle (good) or fat (check waist trend)
- **All measurements increasing proportionally + Strength UP**: Likely clean bulk
- **Measurements decreasing + Strength maintained**: Cutting fat while preserving muscle (ideal)

**Cardiovascular Health Indicators:**
- **Resting Heart Rate DECREASING**: Excellent - cardiovascular fitness improving
  - Well-trained athletes: 40-60 bpm
  - Average fitness: 60-80 bpm
  - Needs improvement: 80+ bpm
  - If RHR improving: Cardio capacity increasing, recovery improving
- **Blood Pressure IMPROVING** (moving toward 120/80 or lower):
  - Cardiovascular health improving
  - Exercise program working well
  - May indicate fat loss, better diet, reduced stress
- **Blood Pressure or RHR WORSENING**:
  - May indicate overtraining, poor recovery, excessive stress
  - Consider adding rest day, reducing volume, improving sleep
  - May need more cardio/conditioning work if BP is high

**3. RECOVERY & OVERTRAINING INDICATORS:**
Look for signs of inadequate recovery:
- Difficulty ratings consistently 8-10 across multiple workouts
- Strength decreasing despite good effort
- Workouts frequently skipped or incomplete
- Body weight dropping with body fat stable/increasing (stress, poor sleep, overtraining)
${previousWeekData.startMeasurements?.avgSleepHours && userData.avgSleepHours ? `- Sleep: ${previousWeekData.startMeasurements.avgSleepHours} hrs → ${userData.avgSleepHours} hrs (${userData.avgSleepHours < 7 ? 'INSUFFICIENT - major recovery issue!' : userData.avgSleepHours >= 8 ? 'excellent' : 'adequate'})` : ''}
${previousWeekData.startMeasurements?.stressLevel && userData.stressLevel ? `- Stress Level: ${previousWeekData.startMeasurements.stressLevel}/10 → ${userData.stressLevel}/10 (${userData.stressLevel >= 7 ? 'HIGH - may need deload' : userData.stressLevel <= 4 ? 'low - good for training' : 'moderate'})` : ''}
${previousWeekData.startMeasurements?.energyLevel && userData.energyLevel ? `- Energy Level: ${previousWeekData.startMeasurements.energyLevel}/10 → ${userData.energyLevel}/10 (${userData.energyLevel <= 4 ? 'LOW - reduce volume' : userData.energyLevel >= 7 ? 'high - ready to train hard' : 'moderate'})` : ''}

**CRITICAL RECOVERY ANALYSIS:**
- If sleep < 7 hours AND (stress >= 7 OR energy <= 4): REDUCE VOLUME BY 30%, focus on recovery
- If sleep >= 8 hours AND stress <= 4 AND energy >= 7: Can push hard, progressive overload
- If energy LOW despite good sleep: Check nutrition, may need more calories or rest day

If these appear:
- Reduce training volume by 15-25% (fewer sets or exercises)
- Add extra rest day or active recovery
- Recommend sleep optimization and stress management
- Consider nutrition is insufficient for training load

**4. GOAL-SPECIFIC INTELLIGENT ADJUSTMENTS:**

For **Muscle Building** (${primaryGoal === 'muscle' ? 'THIS IS THEIR GOAL' : ''}):
- Optimal: 6-12 reps, difficulty 6-8
- If gaining weight but body fat increasing: Reduce surplus slightly, add conditioning
- If not gaining weight: Increase calories, especially carbs around training
- Volume is key: 12-20 sets per muscle group per week

For **Strength** (${primaryGoal === 'strength' ? 'THIS IS THEIR GOAL' : ''}):
- Optimal: 4-8 reps, difficulty 7-9, longer rest (2-4 min)
- Focus on compound movements with progressive weight
- If stalling: May need deload week or more calories

For **Fat Loss/Toning** (${primaryGoal === 'weight_loss' || primaryGoal === 'toned' ? 'THIS IS THEIR GOAL' : ''}):
- If weight not decreasing: Reduce calories by 100-200, increase daily activity
- If losing weight too fast (>2 lbs/week): Increase protein, slight calorie increase to preserve muscle
- If strength dropping significantly: Too aggressive, increase calories slightly
- 12-15 reps, moderate weight, shorter rest, add metabolic finishers

For **Athletic Performance** (${primaryGoal === 'athletic' ? 'THIS IS THEIR GOAL' : ''}):
- Balance strength, power, and conditioning
- If performance stalling: Check recovery and nutrition timing

**5. NUTRITION ADJUSTMENTS:**
Based on body composition trends, goals, AND actual intake data:

${previousWeekData.nutritionSummary && previousWeekData.nutritionSummary.daysLogged > 0 ? `
**Use This Week's ACTUAL Nutrition Data:**
- User's actual average intake: ${previousWeekData.nutritionSummary.avgCalories} cal, ${previousWeekData.nutritionSummary.avgProtein}g protein
- Previous targets: ${previousWeekData.avgCalorieTarget} cal, ${previousWeekData.avgProteinTarget}g protein
- Variance: ${previousWeekData.nutritionSummary.calorieVariance > 0 ? '+' : ''}${previousWeekData.nutritionSummary.calorieVariance} cal, ${previousWeekData.nutritionSummary.proteinVariance > 0 ? '+' : ''}${previousWeekData.nutritionSummary.proteinVariance}g protein

**Intelligent Adjustments Based on Results:**
- If weight/body fat changed as expected AND user hit targets: Keep similar targets
- If weight/body fat changed as expected BUT user was over/under target: Adjust targets to match their actual intake (they found a sustainable level)
- If results don't match goal (e.g., should lose weight but gained):
  ${previousWeekData.nutritionSummary.calorieVariance > 100 ? '- User is eating MORE than target - this explains lack of fat loss. Reduce target by 200-300 cal' : ''}
  ${previousWeekData.nutritionSummary.calorieVariance < -200 ? '- User is eating LESS than target - may be losing muscle. Increase target by 100-200 cal, especially protein' : ''}
  ${previousWeekData.nutritionSummary.proteinVariance < -20 ? '- User is not meeting protein needs - increase protein target by 20-30g and emphasize importance' : ''}
- Adjust carb timing around training days for better performance and recovery
` : `
- Recalculate TDEE based on new weight
- If actual results don't match expected (e.g., should be losing fat but gaining):
  - Reduce calories by 10-15%
  - Increase protein by 20-30g
  - Adjust carb timing around workouts
- **User did not track nutrition - STRONGLY encourage tracking for next week**
`}
- Provide specific, actionable nutrition guidance in the plan

**YOUR JOB:** Synthesize ALL this data into intelligent, individualized adjustments. Don't follow formulas blindly - think like an experienced coach who understands the big picture.

**NUTRITION ADHERENCE:**
${previousWeekData.nutritionSummary && previousWeekData.nutritionSummary.daysLogged > 0 ? `
- Days Logged: ${previousWeekData.nutritionSummary.daysLogged} of 7
- Average Calorie Intake: ${previousWeekData.nutritionSummary.avgCalories} cal/day (Target: ${previousWeekData.avgCalorieTarget} cal/day)
- Calorie Variance: ${previousWeekData.nutritionSummary.calorieVariance > 0 ? '+' : ''}${previousWeekData.nutritionSummary.calorieVariance} cal/day (${previousWeekData.nutritionSummary.calorieVariance > 0 ? 'OVER' : 'UNDER'} target by ${Math.abs(previousWeekData.nutritionSummary.calorieVariance)} cal)
- Average Protein Intake: ${previousWeekData.nutritionSummary.avgProtein}g/day (Target: ${previousWeekData.avgProteinTarget}g/day)
- Protein Variance: ${previousWeekData.nutritionSummary.proteinVariance > 0 ? '+' : ''}${previousWeekData.nutritionSummary.proteinVariance}g/day
- Days On Target (within ±10%): ${previousWeekData.nutritionSummary.daysOnTarget} of ${previousWeekData.nutritionSummary.daysLogged} days

**NUTRITION ANALYSIS - CRITICAL:**
${previousWeekData.nutritionNotes}
` : `
- Average Calorie Target: ${previousWeekData.avgCalorieTarget} cal/day
- Average Protein Target: ${previousWeekData.avgProteinTarget}g/day
- ⚠️ User did not log nutrition this week - cannot assess adherence. STRONGLY encourage nutrition tracking in feedback.
`}

**RECOVERY METRICS:**
${userData.muscleSoreness || userData.fatigueLevel ? `
- Muscle Soreness Level: ${userData.muscleSoreness}/10 ${userData.muscleSoreness >= 8 ? '(VERY HIGH - risk of overtraining)' : userData.muscleSoreness >= 6 ? '(ELEVATED - monitor closely)' : userData.muscleSoreness >= 4 ? '(NORMAL - good recovery)' : '(LOW - recovering well)'}
- Overall Fatigue Level: ${userData.fatigueLevel}/10 ${userData.fatigueLevel >= 8 ? '(EXHAUSTED - reduce volume immediately)' : userData.fatigueLevel >= 6 ? '(TIRED - consider deload)' : userData.fatigueLevel >= 4 ? '(MODERATE - manageable)' : '(ENERGIZED - ready to train hard)'}

**RECOVERY ANALYSIS - CRITICAL FOR PROGRAMMING:**
${userData.muscleSoreness >= 8 || userData.fatigueLevel >= 8 ? `
⚠️ **SEVERE RECOVERY DEFICIT DETECTED**
- Muscle soreness ${userData.muscleSoreness >= 8 ? 'is VERY HIGH (' + userData.muscleSoreness + '/10)' : ''}${userData.muscleSoreness >= 8 && userData.fatigueLevel >= 8 ? ' and ' : ''}${userData.fatigueLevel >= 8 ? 'fatigue is EXTREME (' + userData.fatigueLevel + '/10)' : ''}
- **MANDATORY ACTIONS:**
  1. REDUCE training volume by 40-50% for next week
  2. ADD an extra rest day or convert one training day to active recovery only
  3. Focus on lighter weights, higher reps, better form
  4. Emphasize sleep (8+ hours), hydration, and stress management
  5. Check nutrition - may be under-eating for training load
  6. Consider this a DELOAD WEEK to prevent injury and burnout
- User is showing signs of overtraining - prioritize recovery over progression
` : userData.muscleSoreness >= 6 || userData.fatigueLevel >= 6 ? `
⚠️ **ELEVATED RECOVERY NEEDS**
- Recovery metrics are elevated (soreness: ${userData.muscleSoreness}/10, fatigue: ${userData.fatigueLevel}/10)
- **RECOMMENDED ACTIONS:**
  1. Maintain or slightly reduce training volume (reduce by 10-15%)
  2. Focus on recovery: adequate sleep, nutrition, hydration
  3. Consider reducing intensity on 1-2 workouts this week
  4. Avoid adding new high-intensity exercises
  5. May need extra rest day if symptoms persist
- Do NOT increase volume or intensity this week
` : userData.muscleSoreness >= 4 || userData.fatigueLevel >= 4 ? `
✓ **NORMAL RECOVERY STATE**
- Recovery metrics are in normal range (soreness: ${userData.muscleSoreness}/10, fatigue: ${userData.fatigueLevel}/10)
- Can maintain current training volume and apply progressive overload
- Monitor closely - if soreness/fatigue increase next week, back off slightly
` : `
✓ **EXCELLENT RECOVERY**
- User is recovering very well (soreness: ${userData.muscleSoreness}/10, fatigue: ${userData.fatigueLevel}/10)
- Can confidently increase training volume or intensity
- Body is adapting well to current training load
- Great opportunity for progressive overload
`}
` : `
- Recovery metrics not provided this week
- Use sleep, stress, and energy levels as recovery indicators instead
`}

**ASSESSMENT & ADJUSTMENTS:**
Based on the data above:
1. Analyze progress toward their goal: ${primaryGoal}
2. Identify what's working and what needs adjustment
3. **IMPORTANT: Rotate exercises to prevent plateaus and boredom** - Change at least 40-50% of exercises from last week while maintaining focus on target areas
4. Adjust workout intensity, volume, or exercise selection based on performance AND recovery metrics
5. Recalculate nutrition needs based on new measurements, progress, and actual intake vs targets
6. Provide motivation and specific feedback on their progress

**DELOAD WEEK ASSESSMENT:**
Check if ${displayName} needs a deload week (reduce volume by 40-50%, keep intensity):
- If Week ${previousWeekData.weekNumber} >= 4: Consider scheduling deload
- If difficulty ratings consistently 8-10 for 2+ weeks: DELOAD NEEDED
- If strength declining despite good effort: DELOAD NEEDED
- If multiple workouts skipped or incomplete: DELOAD NEEDED
${userData.muscleSoreness >= 8 || userData.fatigueLevel >= 8 ? '- **RECOVERY METRICS INDICATE DELOAD NEEDED** (soreness: ' + userData.muscleSoreness + '/10, fatigue: ' + userData.fatigueLevel + '/10)' : ''}
${userData.muscleSoreness >= 6 || userData.fatigueLevel >= 6 ? '- Recovery metrics are elevated - consider reduced volume week' : ''}
- Deload week means: Same exercises, 50% of sets/reps, focus on form and recovery
- After deload, resume normal programming with renewed energy

**EXERCISE VARIETY REQUIREMENTS:**
- Rotate exercises each week to keep the body adapting and prevent boredom
- Keep some core compound movements but vary accessory exercises
- Use different equipment when possible (machines, dumbbells, cables, bodyweight)
- Vary rep ranges and set schemes to provide new stimulus
` : ''}

**GOALS:**
- Primary Goal: ${primaryGoal}
- Target Areas: ${targetAreas && targetAreas.length > 0 ? targetAreas.join(', ') : 'Full body (arms, chest, shoulders, back, legs, core)'}
- Timeframe: ${timeframe || '12 weeks'}

**AVAILABILITY:**
- ${daysPerWeek || 5} days per week
- Session duration: ${timePerSession || '45-60 minutes'}
- Home workout time: ${homeWorkoutTime || 'early morning'}
- Gym workout time: ${gymWorkoutTime || 'morning'}
${gymDays && gymDays.length > 0 ? `- **Preferred gym days: ${gymDays.join(', ')}** - MUST schedule gym workouts on these days` : ''}
${restDays && restDays.length > 0 ? `- **Preferred rest days: ${restDays.join(', ')}** - MUST schedule rest on these days` : ''}
- Gym access: ${gymAccess && gymAccess.length > 0 ? gymAccess.join(', ') : 'Home only (bodyweight training)'}
- Home equipment: ${equipment && equipment.length > 0 ? equipment.join(', ') : 'None (bodyweight only)'}

**YOUR TASK:**
Design a comprehensive 7-day training program specifically for ${displayName}. This needs to be:

1. **Realistic & Sustainable** - Fits their schedule and facilities
2. **Goal-Oriented** - Directly addresses their primary goal and target areas
3. **Progressive** - Starts at their current level, builds systematically
4. **Specific** - Uses actual equipment they have access to
5. **Balanced** - Includes rest, recovery, and variety

**IMPORTANT CONSIDERATIONS:**
- If they have Planet Fitness access, use actual PF machines (leg press, chest press, lat pulldown, etc.)
- If home-only, use bodyweight + any equipment they listed
${gymDays && gymDays.length > 0 ? `- **CRITICAL: Schedule gym/Planet Fitness workouts ONLY on: ${gymDays.join(', ')}. Schedule home workouts on the remaining days.**` : ''}
- Match intensity to their current fitness level (${pullups} pull-ups, ${pushups} push-ups)
- ${gender === 'female' ? 'Consider female-specific programming (typically more lower body volume, different rep ranges)' : 'Focus on compound movements and progressive overload'}
- Home workouts scheduled for ${homeWorkoutTime || 'early morning'}, gym workouts for ${gymWorkoutTime || 'morning'}
- Keep sessions within the ${timePerSession} duration range (warm-up + workout + cooldown)

**CRITICAL - HOME EQUIPMENT USAGE:**
${displayName} has invested in quality home gym equipment. You MUST design home workouts that maximize this equipment:

${equipment && equipment.includes('dumbbells') ? `
**DUMBBELLS - Core Home Equipment:**
- Use dumbbells for compound movements: dumbbell bench press, dumbbell rows, goblet squats, Romanian deadlifts, shoulder press, etc.
- Dumbbell exercises should be the PRIMARY exercises in home workouts, not just accessories
- Include unilateral work: single-arm rows, Bulgarian split squats, single-leg RDLs
- Progressive overload: Track weights used and increase by 2.5-5 lbs when appropriate
- Examples: Dumbbell bench press (3×8-12), Dumbbell rows (3×10-12), Goblet squats (4×10-15), Dumbbell shoulder press (3×8-12)
` : ''}

${equipment && equipment.includes('weight_bench') ? `
**ADJUSTABLE BENCH - Game Changer:**
- Use the adjustable bench for MULTIPLE angles: flat, incline (30°, 45°), decline
- Flat bench: Dumbbell bench press, dumbbell flyes, skull crushers, pullovers
- Incline bench (30-45°): Incline dumbbell press (targets upper chest), incline rows, incline curls
- Decline bench: Decline push-ups, decline sit-ups, decline dumbbell press
- Seated position: Shoulder press, seated rows, concentration curls, overhead tricep extensions
- DON'T waste this equipment - every upper body home workout should use the bench creatively
` : ''}

${(equipment && equipment.includes('pull_up_bar')) || (equipment && equipment.includes('dip_station')) ? `
**PULL-UP/DIP BAR - Elite Bodyweight Strength:**
- Pull-up bar exercises: Pull-ups (various grips), chin-ups, hanging leg raises, hanging knee raises, Australian pull-ups (if bar allows)
- Dip bar exercises: Dips (chest emphasis and tricep emphasis), L-sits, knee raises, support holds
- Use resistance bands for assisted pull-ups/dips if needed (progressive overload by using lighter bands over time)
- Progressive overload: Track reps achieved, work toward unassisted sets, then add weight with backpack/dumbbell between legs
- EVERY home workout should include at least 1-2 pull-up bar exercises (pull-ups, chin-ups, or variations)
- Dips should be programmed 2-3x per week as a primary pushing movement
- Examples: Pull-ups (3×AMRAP), Weighted dips (3×8-12), Hanging leg raises (3×10-15)
` : ''}

${equipment && equipment.includes('resistance_bands') ? `
**RESISTANCE BANDS - Versatility:**
- Use bands for warm-up activation (band pull-aparts, band face pulls)
- Use bands for assisted pull-ups and dips (progressive overload by reducing band thickness)
- Use bands for additional resistance on bodyweight exercises
- Band exercises: face pulls, pull-aparts, lateral raises, banded push-ups, banded squats
` : ''}

**HOME WORKOUT DESIGN PRINCIPLES:**
1. **Dumbbells + Bench = Full Body Gym**: This combination allows nearly every movement pattern
2. **Pull-up/Dip Bar = Back & Push Mastery**: These are among the BEST exercises for upper body development
3. **Bodyweight + Equipment = Complete Training**: Blend equipment-based progressive overload with bodyweight skill work
4. **Programming Priority**:
   - Home workouts should feel JUST AS EFFECTIVE as gym workouts
   - Use dumbbells for PRIMARY compound lifts where progressive overload matters most (bench press, rows, squats, RDLs)
   - Include pull-ups/dips as anchor exercises (typically first or second exercise)
   - Use bodyweight exercises for skill work, conditioning, high-rep burnouts, and variation
   - Bench angles provide variety (flat, incline, decline = 3 different exercises)
5. **Progressive Overload at Home**:
   - Track dumbbell weights precisely for compound lifts
   - Track pull-up/dip reps and add weight when hitting 12+ reps
   - Use bodyweight progressions (regular push-ups → archer push-ups → one-arm negatives)
   - Use tempo variations (slow eccentric) when weight isn't available
   - Use bench angles to make exercises harder/easier

**EXAMPLE HOME WORKOUT STRUCTURE:**
- Start with compound pull-up bar exercise (e.g., Pull-ups 3×AMRAP)
- Follow with dumbbell compound pressing (e.g., Dumbbell Bench Press 3×8-12)
- Add dumbbell compound pulling (e.g., Dumbbell Rows 3×10-12 each arm)
- Include lower body with dumbbells (e.g., Goblet Squats 4×10-15)
- Add bodyweight pushing variation (e.g., Decline Push-ups 3×12-15, Pike Push-ups 3×10, or Dips 3×8-12)
- Add bodyweight core/conditioning (e.g., Plank variations, mountain climbers, burpees)
- Finish with bodyweight skill work or burnouts

**BODYWEIGHT EXERCISE INTEGRATION:**
- **Primary Compound Lifts**: Use dumbbells/bench for progressive overload (bench press, rows, squats, shoulder press)
- **Bodyweight Exercises**: Use for variation, skill development, conditioning, and high-rep work
- **Good uses of bodyweight**:
  - Push-up variations (decline, pike, archer, diamond) as accessory pushing or finishers
  - Core work (planks, hollow body holds, L-sits on dip bar)
  - Conditioning finishers (burpees, mountain climbers, jump squats)
  - Skill development (handstand progressions, pistol squat progressions)
  - High-rep burnouts (push-ups to failure, bodyweight squats)
  - Supersets with weighted exercises (e.g., dumbbell press + push-ups to failure)
- **Balance**: 60-70% equipment-based compound work, 30-40% bodyweight for variation/conditioning

**PROGRAM SMART HOME WORKOUTS** - ${displayName} has equipment for progressive overload AND loves bodyweight training. Blend both!

**NUTRITION CALCULATION:**
Based on ${displayName}'s stats:
- Current weight: ${weight} lbs
- Height: ${height}" (${Math.floor(height / 12)}' ${height % 12}")
- Age: ${age}, Gender: ${gender}
${bodyFat ? `- Body Fat: ${bodyFat}%` : ''}
- Primary Goal: ${primaryGoal}
- Activity Level: ${daysPerWeek} training days per week

Calculate ${displayName}'s daily nutrition needs:
1. **BMR** (Basal Metabolic Rate) using Mifflin-St Jeor equation
2. **TDEE** (Total Daily Energy Expenditure) based on activity level
3. **Calorie Goal** adjusted for their primary goal:
   - Weight loss: -300 to -500 calories from TDEE
   - Muscle gain: +200 to +400 calories from TDEE
   - Maintenance/recomp: at or slightly below TDEE
4. **Protein Goal** based on lean body mass and goals:
   - Weight loss: 1.0-1.2g per lb of bodyweight
   - Muscle gain: 0.8-1.0g per lb of bodyweight
   - Maintenance: 0.7-0.9g per lb of bodyweight
5. **Daily Variance** - Adjust calories based on training vs rest days:
   - Training days: +10-15% calories
   - Rest days: -10-15% calories

**COACHING INSIGHTS REQUIREMENTS:**

This is CRITICAL - you are not just a program generator, you are ${displayName}'s personal coach. Every week and every workout needs personalized coaching insights that explain your strategy and reasoning.

**Weekly Strategy Overview:**
At the start of each week's program, provide a comprehensive coaching overview (3-4 paragraphs) that covers:
1. **Analysis of Last Week** (if weekly review): What the data tells you about their progress, recovery, and adherence
2. **This Week's Primary Goals**: What you're trying to accomplish this week (strength gains, fat loss, recovery, skill development, etc.)
3. **Strategic Approach**: WHY you're programming the way you are - what's the logic behind exercise selection, volume, intensity, split, etc.
4. **Key Focus Areas**: Specific things ${displayName} should pay attention to this week (form on certain lifts, nutrition timing, sleep, etc.)

**Daily Coaching Insights:**
For EVERY training day, write a personalized paragraph (3-5 sentences) that explains:
- What this day's focus is (muscle groups, movement patterns, energy systems)
- HOW this workout contributes to their specific goal (${primaryGoal})
- WHY you chose these specific exercises and set/rep schemes
- What they should focus on during this session (mind-muscle connection, explosive power, tempo, etc.)
- How this fits into the weekly strategy

**IMPORTANT GUIDELINES FOR COACHING INSIGHTS:**
- Write in first person as their coach: "I'm focusing on..." or "I've programmed this because..."
- Reference SPECIFIC data from their previous week (e.g., "Since your bench press increased by 10 lbs last week...")
- Connect exercises to their goals (e.g., "Bulgarian split squats will help correct the leg strength imbalance I noticed")
- Be conversational and motivating, not robotic or generic
- Show that you're THINKING about their unique situation
- Reference their recovery state, nutrition adherence, or other metrics when relevant
${isWeeklyReview ? `- Address any issues from last week (low adherence, high soreness, nutrition struggles, etc.)` : ''}

**OUTPUT FORMAT:**
Return a JSON object with this exact structure:

\`\`\`json
{
  "programSummary": "${isWeeklyReview ? `Week ${weekNumber + 1} adjustments based on progress. 2-3 sentences on what's changing and why.` : `2-3 sentence overview of the program philosophy and why it fits ${displayName}`}",
  "weeklyStrategyOverview": "3-4 paragraph comprehensive coaching overview explaining your strategic approach for this week. Reference specific data from their previous performance, explain your reasoning for the program design, highlight what you want them to focus on, and connect everything to their goal of ${primaryGoal}. Be specific, personal, and insightful - show that you're analyzing their unique situation.",
  ${isWeeklyReview ? `"weeklyFeedback": {
    "progressAssessment": "2-3 sentences analyzing their progress this week",
    "strengths": "What they did well this week",
    "areasToImprove": "What needs attention next week",
    "motivationalMessage": "Personalized encouragement for ${displayName}"
  },` : ''}
  "weeklyPlan": {
    "monday": {
      "name": "Workout Name",
      "time": "Specific time (e.g., 5:00 AM for home, 9:00 AM for gym)",
      "duration": "45-60 min",
      "location": "Planet Fitness or Home",
      "focus": "Body parts",
      "coachingInsight": "3-5 sentence personalized coaching insight for this day. Explain what the focus is, WHY you chose these exercises, HOW this contributes to their ${primaryGoal} goal, what they should focus on during the workout, and how this fits into the weekly strategy. Reference specific data if available (e.g., 'Since your squat difficulty was 8/10 last week, I've reduced volume slightly...'). Be conversational and motivating.",
      "calorieGoal": 2200,
      "proteinGoal": 160,
      "nutritionNotes": "Training day - higher carbs for energy and recovery",
      "warmUp": [
        {
          "name": "Warm-up exercise (e.g., Arm Circles, Cat-Cow, Leg Swings)",
          "duration": "30-60 seconds or 10-15 reps",
          "notes": "Prepare the body for main workout, increase blood flow"
        }
      ],
      "exercises": [
        {
          "id": 1,
          "name": "Exercise Name",
          "sets": 3,
          "reps": "8-12",
          "rest": "60-90 sec",
          "notes": "Specific form cues and modifications for ${displayName}'s level",
          "progressionTips": "How to progress this exercise",
          "howTo": "Step-by-step instructions on how to perform this exercise correctly (especially important for bodyweight and unfamiliar movements)"
        }
      ],
      "coolDown": [
        {
          "name": "Cool-down stretch (e.g., Hamstring Stretch, Quad Stretch, Shoulder Stretch)",
          "duration": "30-60 seconds per side",
          "notes": "Hold each stretch, breathe deeply, promote recovery"
        }
      ],
      "cardio": {
        "type": "Optional or required based on goal (HIIT, steady-state, none)",
        "duration": "10-20 min",
        "notes": "For fat loss: add HIIT finisher. For muscle gain: minimal cardio. For general fitness: 2-3x per week steady-state.",
        "exercises": [
          {
            "name": "Cardio exercise (e.g., Jump Rope, Burpees, Treadmill)",
            "duration": "Work time",
            "intensity": "High/Moderate/Low",
            "notes": "Specific instructions"
          }
        ]
      },
      "homeAlternative": {
        "name": "Home Alternative Workout Name",
        "warmUp": [
          {
            "name": "Home warm-up (bodyweight)",
            "duration": "30-60 seconds",
            "notes": "Same principles as gym warm-up"
          }
        ],
        "exercises": [
          {
            "id": 1,
            "name": "Home Exercise (bodyweight or with equipment they have)",
            "sets": 3,
            "reps": "8-12",
            "rest": "60-90 sec",
            "notes": "Home-specific form cues",
            "progressionTips": "How to progress at home",
            "howTo": "Step-by-step instructions on how to perform this exercise correctly (especially important for bodyweight and unfamiliar movements)"
          }
        ],
        "coolDown": [
          {
            "name": "Home cool-down stretch",
            "duration": "30-60 seconds",
            "notes": "Same stretches as gym version"
          }
        ],
        "cardio": {
          "type": "Home cardio option",
          "duration": "10-20 min",
          "exercises": [
            {
              "name": "Bodyweight cardio (e.g., Jumping Jacks, Mountain Climbers)",
              "duration": "Work time",
              "intensity": "High/Moderate",
              "notes": "No equipment needed"
            }
          ]
        }
      }
    },
    "tuesday": {
      "name": "Rest Day / Active Recovery",
      "time": "All Day",
      "duration": "N/A",
      "location": "Recovery",
      "focus": "Rest & Recovery",
      "coachingInsight": "2-3 sentence insight explaining WHY this rest day is placed here strategically, what recovery focus they should have (sleep, nutrition, hydration, mobility), and how this supports their training goals. Reference their recovery metrics if available (e.g., 'Your soreness was 7/10 last week, so this rest day is crucial...').",
      "calorieGoal": 1900,
      "proteinGoal": 160,
      "nutritionNotes": "Rest day - lower carbs, focus on protein and recovery",
      "exercises": [],
      "activeRecovery": [
        {
          "name": "Light walk or yoga or stretching session",
          "duration": "20-30 min",
          "notes": "Optional light activity to promote blood flow and recovery. NO intense exercise."
        },
        {
          "name": "Foam rolling",
          "duration": "10-15 min",
          "notes": "Target sore muscle groups from previous workouts"
        }
      ],
      "recoveryTips": "Focus on sleep (7-9 hours), hydration, and nutrition. This is when muscle growth happens!"
    },
    "wednesday": { ... },
    "thursday": { ... },
    "friday": { ... },
    "saturday": { ... },
    "sunday": { ... }
  },
  "nutritionSummary": {
    "bmr": 1750,
    "tdee": 2400,
    "weeklyAverageCalories": 2050,
    "weeklyAverageProtein": 160,
    "macroSplit": "40% carbs, 30% protein, 30% fat",
    "rationale": "2-3 sentences explaining why these targets fit ${displayName}'s goals"
  },
  "nutritionGuidance": "Brief 2-3 sentence nutrition advice for their goal, including meal timing suggestions",
  "progressionPlan": "How to progress week-to-week for the next ${timeframe}"
}
\`\`\`

**IMPORTANT: For ALL gym workouts, include a "homeAlternative" object with equivalent exercises that can be done at home using bodyweight or equipment they listed (${equipment.join(', ')}). This allows them to switch between gym and home without additional API calls.**

**WORKOUT DISTRIBUTION:**
- ${daysPerWeek} training days
- ${7 - parseInt(daysPerWeek)} rest/active recovery days
- Balance upper/lower body or full body based on their goals

**WARM-UP REQUIREMENTS (CRITICAL FOR INJURY PREVENTION):**
- EVERY training day must include 3-5 warm-up exercises (5-7 minutes total)
- Focus on mobility and activation for muscles about to be trained
- Examples: Arm circles, leg swings, cat-cow, hip circles, band pull-aparts, bodyweight squats
- Match warm-up to workout focus (e.g., shoulder mobility before upper body day)

**COOL-DOWN REQUIREMENTS (CRITICAL FOR RECOVERY):**
- EVERY training day must include 3-5 stretches (5-7 minutes total)
- Static stretching for muscles worked during the session
- Hold each stretch 30-60 seconds
- Examples: Hamstring stretch, quad stretch, chest doorway stretch, shoulder stretch, hip flexor stretch

**CARDIO/CONDITIONING INTEGRATION:**
- For **FAT LOSS/TONING** goals: Add HIIT finisher 2-3x per week (10-15 min burpees, jump rope, sprints)
- For **MUSCLE BUILDING** goals: Minimal cardio, maybe 1-2x light steady-state to maintain conditioning
- For **GENERAL FITNESS/ATHLETIC** goals: 2-3x moderate cardio (20-30 min)
- For **STRENGTH** goals: Minimal cardio, focus on lifting
- Cardio should be separate section in JSON, can be optional or required based on goal

**MAIN WORKOUT:**
- **CRITICAL**: Each training day MUST have 6-10 main exercises (not counting warm-up/cool-down/cardio)
- **DO NOT** create workouts with only 1-2 exercises - this is insufficient volume
- If creating a beginner program, still include 6+ exercises but adjust sets/reps/intensity
- Rest days should have exercises: [] but training days need full workout

**VALIDATION CHECKLIST - VERIFY BEFORE RETURNING:**
✓ Each training day has 6-10 exercises in the exercises array
✓ Each exercise has: id, name, sets, reps, rest, notes, progressionTips, howTo
✓ Nutrition goals are calculated (not placeholder values)
✓ All JSON is valid and parseable
✓ Duration matches actual workout length (6-10 exercises = 45-75 minutes)

Return ONLY valid JSON, no other text.`;
}
